import { useRef, useState } from "react";
import { useModalDismiss } from "../../controllers/useModalDismiss";
import type { DownloadsController } from "../../controllers/useDownloads";
import { FileDrop } from "../components/FileDrop";
import {
  DESKTOP_PLATFORMS,
  formatBytes,
  PLATFORM_LABEL,
  type DesktopApp,
  type DesktopPlatform,
} from "../../models/downloads";

// The 다운로드 자료실's admin surface: register/edit a desktop app (이름·설명·아이콘) and publish a new
// installer (버전·플랫폼·파일·릴리스 노트). Only platform admins ever see these — and the backend
// rejects the calls regardless, since they live under /api/admin/**.

/**
 * Register a new desktop app, or edit an existing one. The same dialog does both because the fields
 * are identical; passing `app` switches it to edit mode (the key is immutable once assigned, so it
 * is only offered on registration).
 */
export function DesktopAppWizard({ c, app, onClose }: {
  c: DownloadsController;
  app?: DesktopApp;
  onClose: () => void;
}) {
  const editing = !!app;
  const [name, setName] = useState(app?.name ?? "");
  const [slug, setSlug] = useState(app?.slug ?? "");
  const [description, setDescription] = useState(app?.description ?? "");
  const [icon, setIcon] = useState(app?.icon ?? "");
  const [iconFile, setIconFile] = useState<File | null>(null);
  // Registration defaults to NOT published: a brand-new app has no installer yet, and shipping an
  // empty card to every user is worse than making the admin flip one switch when it's ready.
  const [published, setPublished] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  useModalDismiss(cardRef, onClose, submitting);

  async function submit() {
    if (!name.trim()) {
      setError("앱 이름을 입력하세요.");
      return;
    }
    setSubmitting(true);
    setError(null);
    let result = editing
      ? await c.update(app.id, {
        name: name.trim(),
        description: description.trim(),
        icon: icon.trim(),
      })
      : await c.register({
        name: name.trim(),
        slug: slug.trim() || undefined,
        description: description.trim() || undefined,
        icon: icon.trim() || undefined,
        published,
      });
    // The icon image is a second call (multipart), and only for the edit path — a brand-new app has
    // no id to attach it to until it exists.
    if (result.ok && editing && iconFile) {
      result = await c.uploadIcon(app.id, iconFile);
    }
    setSubmitting(false);
    if (result.ok) onClose();
    else setError(result.error ?? "저장에 실패했습니다.");
  }

  return (
    <div
      className="wz-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={editing ? "앱 편집" : "앱 등록"}
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div className="wz-card" ref={cardRef} onClick={(e) => e.stopPropagation()}>
        <div className="wz-head">
          <h2>{editing ? "앱 편집" : "데스크톱 앱 등록"}</h2>
          <button className="wz-x" aria-label="닫기" onClick={onClose} disabled={submitting}>✕</button>
        </div>

        <div className="wz-body">
          {error && <div className="wz-error" role="alert">{error}</div>}

          <section className="wz-section">
            <h3>기본 정보</h3>
            <label className="wz-field">
              <span className="wz-label">앱 이름 *</span>
              <input className="wz-input" autoFocus value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="예) compath Desktop" />
            </label>
            {!editing && (
              <label className="wz-field">
                <span className="wz-label">식별 키</span>
                <input className="wz-input" value={slug} onChange={(e) => setSlug(e.target.value)}
                       placeholder="비워두면 이름에서 자동 생성 (예: compath-desktop)" />
                <span className="wz-hint">URL·저장 경로에 쓰이는 고정 키입니다. 등록 후에는 바꿀 수 없습니다.</span>
              </label>
            )}
            <label className="wz-field">
              <span className="wz-label">앱 설명</span>
              <textarea className="wz-input" rows={4} value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="이 앱이 무엇을 하는지, 누가 언제 쓰면 되는지 적어주세요." />
            </label>
            {!editing && (
              <label className="wz-field dl-publish-opt">
                <span>
                  <input type="checkbox" checked={published}
                         onChange={(e) => setPublished(e.target.checked)} />
                  {" "}등록 즉시 사용자에게 공개
                </span>
                <span className="wz-hint">
                  체크하지 않으면 관리자에게만 보입니다. 설치 파일을 올린 뒤 공개하세요.
                </span>
              </label>
            )}
          </section>

          <section className="wz-section">
            <h3>아이콘</h3>
            <label className="wz-field">
              <span className="wz-label">아이콘 문자</span>
              <input className="wz-input" value={icon} onChange={(e) => setIcon(e.target.value)}
                     maxLength={4} placeholder="예) 🖥" />
              <span className="wz-hint">이미지를 올리지 않았을 때 카드에 표시할 이모지/글자입니다.</span>
            </label>
            {editing ? (
              <label className="wz-field">
                <span className="wz-label">아이콘 이미지</span>
                <FileDrop
                  compact
                  file={iconFile}
                  accept="image/*"
                  onFile={setIconFile}
                  label="아이콘을 여기에 끌어다 놓으세요"
                  hint="PNG/SVG 권장 · 저장할 때 함께 올라갑니다"
                />
              </label>
            ) : (
              <div className="wz-hint">아이콘 이미지는 앱을 등록한 뒤 &lsquo;앱 편집&rsquo;에서 올릴 수 있습니다.</div>
            )}
          </section>
        </div>

        <div className="wz-foot">
          <button className="btn" onClick={onClose} disabled={submitting}>취소</button>
          <button className="btn primary" onClick={() => void submit()} disabled={submitting}>
            {submitting ? "저장 중…" : editing ? "저장" : "등록"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Publish a new installer for an existing app: 파일 + 버전 + 플랫폼 (+ 릴리스 노트). */
export function PublishReleaseWizard({ c, app, onClose }: {
  c: DownloadsController;
  app: DesktopApp;
  onClose: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState("");
  const [platform, setPlatform] = useState<DesktopPlatform>(c.platform);
  const [releaseNotes, setReleaseNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  useModalDismiss(cardRef, onClose, submitting);

  const duplicate = app.releases.some((r) => r.version === version.trim() && r.platform === platform);

  async function submit() {
    if (!file) {
      setError("설치 파일을 선택하세요.");
      return;
    }
    if (!version.trim()) {
      setError("버전을 입력하세요.");
      return;
    }
    setSubmitting(true);
    setError(null);
    const result = await c.publish(app.id, file, {
      version: version.trim(),
      platform,
      releaseNotes: releaseNotes.trim() || undefined,
    });
    setSubmitting(false);
    if (result.ok) onClose();
    else setError(result.error ?? "업로드에 실패했습니다.");
  }

  return (
    <div
      className="wz-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="새 버전 등록"
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div className="wz-card" ref={cardRef} onClick={(e) => e.stopPropagation()}>
        <div className="wz-head">
          <h2>새 버전 등록 · {app.name}</h2>
          <button className="wz-x" aria-label="닫기" onClick={onClose} disabled={submitting}>✕</button>
        </div>

        <div className="wz-body">
          {error && <div className="wz-error" role="alert">{error}</div>}

          <section className="wz-section">
            <h3>설치 파일</h3>
            <div className="wz-field">
              <FileDrop
                file={file}
                disabled={submitting}
                accept=".exe,.msi,.dmg,.pkg,.zip,.AppImage,.deb,.rpm,.tar.gz"
                onFile={setFile}
                label="설치 파일을 여기에 끌어다 놓으세요"
                hint="exe / dmg / msi / zip 등 배포 파일"
              />
            </div>
          </section>

          <section className="wz-section">
            <h3>버전 정보</h3>
            <div className="wz-row2">
              <label className="wz-field">
                <span className="wz-label">버전 *</span>
                <input className="wz-input" value={version} onChange={(e) => setVersion(e.target.value)}
                       placeholder="예) 1.2.0" />
              </label>
              <label className="wz-field">
                <span className="wz-label">플랫폼</span>
                <select className="wz-input" value={platform}
                        onChange={(e) => setPlatform(e.target.value as DesktopPlatform)}>
                  {DESKTOP_PLATFORMS.map((p) => (
                    <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>
                  ))}
                </select>
              </label>
            </div>
            {duplicate && (
              <div className="wz-hint" style={{ color: "var(--amber)" }}>
                v{version.trim()} ({PLATFORM_LABEL[platform]}) 버전이 이미 있습니다. 다른 버전 번호를 쓰거나 기존 버전을 먼저 삭제하세요.
              </div>
            )}
            <label className="wz-field">
              <span className="wz-label">릴리스 노트</span>
              <textarea className="wz-input" rows={4} value={releaseNotes}
                        onChange={(e) => setReleaseNotes(e.target.value)}
                        placeholder="이 버전에서 달라진 점을 적어주세요." />
            </label>
          </section>

          {c.upload && (
            <div className="wz-hint">
              업로드 중… {formatBytes(c.upload.sent)} / {formatBytes(c.upload.total)}
            </div>
          )}
        </div>

        <div className="wz-foot">
          <button className="btn" onClick={onClose} disabled={submitting}>취소</button>
          <button className="btn primary" onClick={() => void submit()} disabled={submitting || duplicate}>
            {submitting ? "업로드 중…" : "등록"}
          </button>
        </div>
      </div>
    </div>
  );
}
